"use client";

import ServiceTitle from "@/shared/app/ui/text/ServiceTitle";
import { motion } from "framer-motion";
import Image from "next/image";

const warningCards = [
  {
    icon: "/assets/images/page/service/weslo/faceIcon1.svg",
    title: "도로 교통법 위반 주의",
    desc: "불법 설치 주의 경사로가 인도나 도로를 침범하면 도로교통법 위반에 해당합니다.",
  },
  {
    icon: "/assets/images/page/service/weslo/faceIcon2.svg",
    title: "안전사고시 책임 발생",
    desc: "경사로에 걸려 보행자가 넘어질 경우, 건물주나 상점주인의 배상 책임이 발생할 수 있습니다.",
  },
];

const CheckIcon = () => (
  <motion.div
    className="relative w-6 h-6 md:w-11 md:h-11 overflow-hidden"
    initial={{ opacity: 0, y: 40, scale: 0.95 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.8, ease: "easeOut" }}
  >
    <Image
      src="/assets/images/page/service/weslo/checkIcon.svg"
      alt="overview Image"
      fill
      className="object-contain object-center"
    />
  </motion.div>
);

export default function MatterSection() {
  return (
    <div className="bg-[#474173] py-5 md:py-20">
      <div className="w-full xl:max-w-[1280px] mx-auto px-3 xl:px-2">
        <div className="bg-white px-5 py-10 md:py-20 md:px-10 rounded-3xl md:rounded-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <span
              className="text-[#EC6C50] text-base md:text-2xl mb-8 sm:mb-10 md:mb-20 flex tracking-tight"
              style={{ fontFamily: '"paperlogy-bold", sans-serif' }}
            >
              Why it Matters
            </span>
            <p
              className="text-2xl sm:text-3xl md:text-4xl text-[#353535] leading-8  sm:leading-12 md:leading-13 tracking-tight "
              style={{ fontFamily: '"paperlogy-Light", sans-serif' }}
            >
              경사로 설치,
              <br />
              잘못 설치하면{" "}
              <span style={{ fontFamily: '"paperlogy-bold", sans-serif' }}>
                문제
              </span>
              가 발생해요!
            </p>
          </motion.div>
          <motion.div
            className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6 mt-14 md:mt-20"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              hidden: {},
              visible: {
                transition: { staggerChildren: 0.25 },
              },
            }}
          >
            {warningCards.map((card, idx) => (
              <motion.div
                key={idx}
                className="flex flex-col grow overflow-hidden "
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: "easeOut" },
                  },
                }}
              >
                {/* 상단 헤더 */}
                <div className="bg-[#47416F] flex items-center justify-center px-4 py-2 md:py-4 space-x-3 mb-2 md:mb-4 md:rounded-3xl rounded-xl shadow-sm">
                  <div className="w-5 h-5 md:w-8 md:h-8  relative">
                    <Image
                      src={card.icon}
                      alt="faceIcon"
                      fill
                      className="object-contain"
                    />
                  </div>
                  <p
                    className="md:text-xl md:leading-7 text-base leading-6 text-white tracking-tight"
                    style={{ fontFamily: '"pre-Medium", sans-serif' }}
                  >
                    {card.title}
                  </p>
                </div>

                {/* 하단 설명 */}
                <div className="regular-text-18 tracking-tight text-md-14 bg-[#EDEEF3] text-[#47416F] text-center leading-relaxed px-8 py-5 md:py-8 shadow-sm md:rounded-3xl rounded-xl break-words whitespace-pre-line break-keep">
                  {card.desc}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
        <div className="bg-white px-5 py-10 md:py-20 md:px-10 rounded-3xl md:rounded-4xl my-5 flex flex-col items-center">
          <CheckIcon />
          <motion.p
            className="mt-4 text-2xl sm:text-3xl md:text-4xl text-[#353535] leading-8  sm:leading-12 md:leading-12"
            style={{ fontFamily: '"paperlogy-Light", sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            그래서{" "}
            <span style={{ fontFamily: '"paperlogy-bold", sans-serif' }}>
              위슬로
            </span>
            가 해결합니다
          </motion.p>
          <motion.p
            className="mt-5 md:mt-8 text-base md:text-xl xl:text-2xl text-[15px] sm:text-xl sm:leading-8 xl:leading-9 tracking-tight text-[#495368] text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            viewport={{ once: true, amount: 0.3 }}
            style={{ fontFamily: '"pre-Medium", sans-serif' }}
          >
            입간판과 경사로를 하나로!
            <br />두 기능을 결합해 현실적인 해결책을 만들었습니다.
          </motion.p>
          <ul className="flex-col md:flex-row flex space-x-0 space-y-4 md:space-y-0 md:space-x-4 w-full mt-10">
            {[
              { src: "wesloImg1.png", label: "매장 앞 홍보용 입간판" },
              { src: "wesloImg2.png", label: "고정핀 빼면 펼쳐서 사용 가능" },
              { src: "wesloImg3.png", label: "입간판을 펼쳐 경사로로 사용" },
            ].map((item, idx) => (
              <motion.li
                key={idx}
                className="flex-1 relative aspect-[16/9] md:aspect-[16/12] rounded-2xl overflow-hidden flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
              >
                <div className="relative flex-1">
                  <Image
                    src={`/assets/images/page/service/weslo/${item.src}`}
                    alt={item.label}
                    fill
                    className="object-cover object-center"
                  />
                  {/* 이미지 하단 텍스트 */}
                  <div
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-md text-center whitespace-nowrap"
                    style={{ backgroundColor: "rgba(170, 131, 83, 0.45)" }}
                  >
                    <span className="regular-text-16 text-md-14 text-white">
                      {item.label}
                    </span>
                  </div>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
        <div className="bg-white px-5 py-10 md:py-20 md:px-10 rounded-3xl md:rounded-4xl text-center flex flex-col justify-center items-center">
          <CheckIcon />
          <motion.p
            className="mt-5 md:mt-8 text-lg leading-7 md:text-2xl md:leading-9 tracking-tight text-[#474173]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            viewport={{ once: true, amount: 0.3 }}
            style={{ fontFamily: '"paperlogy-Light", sans-serif' }}
          >
            위슬로는 공공에서 설치시,
            <br />
            주기적으로 이동 설치가 가능하며,
            <br />
            설치된 상점 이외에도{" "}
            <span style={{ fontFamily: '"paperlogy-bold", sans-serif' }}>
              주변 상점에서 함께 활용
            </span>
            이 가능합니다.
          </motion.p>
        </div>
      </div>
    </div>
  );
}
